import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI = ["B&B", "Property Manager", "Condominio"];

const initial = { nome: "", tipo_struttura: "B&B", unita: "", email: "", telefono: "" };

const inputCls =
  "w-full min-h-[48px] rounded-lg border border-white/15 bg-[#060B19] px-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-[#FF5500]";

export default function ConvenzioneForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/convenzioni`, form);
      setDone(true);
      toast.success("Richiesta inviata! Controlla la tua email per la conferma.");
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="mt-12 rounded-2xl border border-white/10 bg-[#0D1833] p-6 sm:p-10"
      data-testid="convention-form-container"
    >
      {done ? (
        <div className="flex flex-col items-center py-8 text-center" data-testid="convention-form-success">
          <CheckCircle2 className="mb-4 h-14 w-14 text-[#25D366]" />
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight">
            Richiesta ricevuta!
          </h3>
          <p className="mt-2 max-w-md text-sm text-slate-400 sm:text-base">
            Ti abbiamo appena inviato una email di conferma. Il nostro team B2B ti ricontatterà
            entro 24 ore con la proposta di convenzione.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-8 max-w-xl">
            <p className="font-mono-accent text-xs font-bold uppercase tracking-[0.22em] text-[#FFB800]">
              Modulo Convenzione B2B
            </p>
            <h3 className="font-display mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
              Non usi WhatsApp? <span className="text-[#FF5500]">Scrivici da qui.</span>
            </h3>
            <p className="mt-2 text-sm text-slate-400 sm:text-base">
              Compila il modulo: ricevi subito una conferma via email e ti ricontattiamo entro 24 ore.
            </p>
          </div>

          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="convention-form">
            <div>
              <label htmlFor="conv-nome" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Nome / Ragione Sociale *
              </label>
              <input
                id="conv-nome"
                data-testid="convention-form-name-input"
                required
                minLength={2}
                value={form.nome}
                onChange={set("nome")}
                placeholder="Es. B&B Vesuvio o Studio Rossi Amministrazioni"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="conv-tipo" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Tipo di Struttura *
              </label>
              <select
                id="conv-tipo"
                data-testid="convention-form-type-select"
                value={form.tipo_struttura}
                onChange={set("tipo_struttura")}
                className={inputCls}
              >
                {TIPI.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="conv-unita" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Numero di Immobili / Unità gestite *
              </label>
              <input
                id="conv-unita"
                data-testid="convention-form-units-input"
                required
                value={form.unita}
                onChange={set("unita")}
                placeholder="Es. 3 appartamenti / 1 condominio da 24 unità"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="conv-telefono" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Telefono *
              </label>
              <input
                id="conv-telefono"
                data-testid="convention-form-phone-input"
                required
                type="tel"
                minLength={6}
                value={form.telefono}
                onChange={set("telefono")}
                placeholder="Es. 351 000 0000"
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="conv-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Email di contatto *
              </label>
              <input
                id="conv-email"
                data-testid="convention-form-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="nome@tuastruttura.it"
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="convention-form-submit-button"
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#FF5500] font-bold text-white transition-all duration-200 hover:scale-[1.01] hover:bg-[#ff6622] active:scale-95 disabled:opacity-60 sm:w-auto sm:px-10"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" strokeWidth={2.5} />
                )}
                {loading ? "Invio in corso..." : "Richiedi Proposta di Convenzione"}
              </button>
              <p className="mt-3 text-xs text-slate-500">
                Inviando accetti di essere ricontattato da El Mastro per la proposta di convenzione.
              </p>
            </div>
          </form>
        </>
      )}
    </motion.div>
  );
}
